import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faChevronDown,
  faChevronUp,
  faCircleXmark,
  faGlobe,
} from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";
import { GetKitchenLocations } from "api/rpc/2024-04/facilityAdmin/facility/kitchen/location";
import { IProduct, IVariant } from "redux/reducers/models/product";
import { TKitchenLocation } from "redux/reducers/models/kitchen";
import { LocaleCurrency } from "helpers/Locale";

import { Badge } from "components/badge/Badge";
import DataTable from "components/dataTable/DataTable";
import Checkbox from "components/form/checkbox/Checkbox";

import "./facilityProductTable.scss";

export type ProductSortKey = "title" | "price" | "inventory";
export type ProductSort = { key: ProductSortKey; direction: "asc" | "desc" } | null;

interface IProps {
  products: IProduct[] | undefined;
  sort: ProductSort;
  selectedIds: number[];
  onSelectedIdsChange: (ids: number[]) => void;
  onSortChange: (key: ProductSortKey) => void;
  attachProductToFacility: (event: React.MouseEvent<HTMLElement>, productId: number) => void;
}

function getInventory(product: IProduct, variant: IVariant | undefined): number | null {
  if (!variant || !product.track_inventory) {
    return null;
  }
  return variant.inventory_item?.inventory_level?.quantity_available ?? null;
}

export default function FacilityProductTable(props: IProps) {
  const { products, sort, onSortChange, selectedIds, onSelectedIdsChange } = props;
  const history = useHistory();
  const { t } = useTranslation();

  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const [kitchenLocations, setKitchenLocations] = useState<TKitchenLocation[]>([]);

  const selectableIds = products?.filter(product => product.facility_access).map(product => product.id) ?? [];
  const allSelected = selectableIds.length > 0 && selectedIds.length === selectableIds.length;

  useEffect(() => {
    void loadKitchenLocations();
  }, []);

  useEffect(() => {
    onSelectedIdsChange([]);
    setExpandedIds([]);
  }, [products, onSelectedIdsChange]);

  async function loadKitchenLocations() {
    const res = await GetKitchenLocations({}, false);
    if (res.status === StatusCode.OK) {
      setKitchenLocations(Array.isArray(res.data) ? res.data : [res.data]);
    }
  }

  function toggleId(ids: number[], id: number) {
    return ids.includes(id) ? ids.filter(existing => existing !== id) : [...ids, id];
  }

  function getLocationTitles(product: IProduct) {
    const titles = [
      product.kitchen_location_id,
      product.meta?.kitchen_location_id_2,
      product.meta?.kitchen_location_id_3,
    ]
      .filter(Boolean)
      .map(id => kitchenLocations.find(location => location.id === id)?.title)
      .filter(Boolean);
    return titles.length > 0 ? titles.join(", ") : "-";
  }

  function renderSortHeader(key: ProductSortKey, label: string) {
    const direction = sort?.key === key ? sort.direction : null;
    return (
      <button
        type="button"
        className="facility-product-table-sort"
        aria-sort={direction ? (direction === "asc" ? "ascending" : "descending") : undefined}
        onClick={() => onSortChange(key)}
      >
        {label}
        <FontAwesomeIcon icon={direction === "asc" ? faArrowUp : faArrowDown} />
      </button>
    );
  }

  function renderStatusIcon(icon: typeof faGlobe, label: string, className: string) {
    return (
      <span className={classNames("facility-product-table-status", className)} tabIndex={0} aria-label={label}>
        <FontAwesomeIcon icon={icon} />
        <span className="facility-product-table-tooltip" role="tooltip">
          {label}
        </span>
      </span>
    );
  }

  function renderPrice(variants: IVariant[]) {
    if (variants.length === 0) {
      return "-";
    }
    const prices = variants.map(variant => variant.price);
    const lowest = Math.min(...prices);
    return (
      <>
        <LocaleCurrency amount={lowest} />
        {prices.some(price => price !== lowest) ? "+" : null}
      </>
    );
  }

  function renderProductRows(product: IProduct) {
    const variants = product.variants ?? [];
    const hasMultipleVariants = variants.length > 1;
    const isExpanded = expandedIds.includes(product.id);
    const singleVariant = hasMultipleVariants ? undefined : variants[0];
    const productPath = `/admin/product/${product.id}`;
    const supportText = [
      product.subtitle,
      hasMultipleVariants ? t("{{count}} variants", { count: variants.length }) : null,
    ]
      .filter(Boolean)
      .join(" • ");
    const soldOnline = product.sales_channels?.some(channel => channel.type === "online_store");

    return (
      <React.Fragment key={product.id}>
        <tr className="clickable" onClick={() => history.push(productPath)}>
          <td className="facility-product-table-name-cell">
            <div className="facility-product-table-name">
              <div
                className="facility-product-table-select"
                onClick={event => {
                  event.stopPropagation();
                  if (!product.facility_access) {
                    return;
                  }
                  if (event.target instanceof Element && event.target.closest(".ui-checkbox, .ui-choice")) {
                    return;
                  }
                  onSelectedIdsChange(toggleId(selectedIds, product.id));
                }}
              >
                <Checkbox
                  id={`product-select-${product.id}`}
                  size="small"
                  checked={selectedIds.includes(product.id)}
                  disabled={!product.facility_access}
                  onChange={() => onSelectedIdsChange(toggleId(selectedIds, product.id))}
                />
              </div>
              <div>
                <div className="facility-product-table-title">
                  <span>{product.title}</span>
                  {hasMultipleVariants ? (
                    <button
                      type="button"
                      className="facility-product-table-expand"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? t("Hide variants") : t("Show variants")}
                      onClick={event => {
                        event.stopPropagation();
                        setExpandedIds(prev => toggleId(prev, product.id));
                      }}
                    >
                      <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                    </button>
                  ) : null}
                  {soldOnline ? renderStatusIcon(faGlobe, t("Available in online store"), "is-online") : null}
                  {product.cannot_be_sold || product.meta?.cannot_be_sold
                    ? renderStatusIcon(faCircleXmark, t("Cannot be sold"), "is-unsellable")
                    : null}
                </div>
                {supportText ? <p className="facility-product-table-support">{supportText}</p> : null}
              </div>
            </div>
          </td>
          <td>{singleVariant?.sku || "-"}</td>
          <td>{product.type}</td>
          <td>{renderPrice(variants)}</td>
          <td>
            {!product.facility_access ? (
              <Badge
                type="warning"
                size="small"
                onClick={event => {
                  onSelectedIdsChange([]);
                  props.attachProductToFacility(event, product.id);
                }}
              >
                {t("secure.facility.product.product_table.006")}
              </Badge>
            ) : (
              getInventory(product, singleVariant) ?? "-"
            )}
          </td>
          <td>{getLocationTitles(product)}</td>
        </tr>

        {hasMultipleVariants && isExpanded
          ? variants.map(variant => (
              <tr
                key={`${product.id}-${variant.id}`}
                className="clickable facility-product-table-variant"
                onClick={() => history.push(`${productPath}/variant/${variant.id}`)}
              >
                <td className="facility-product-table-name-cell">↳ {variant.title}</td>
                <td>{variant.sku || "-"}</td>
                <td />
                <td>
                  <LocaleCurrency amount={variant.price} />
                </td>
                <td>{getInventory(product, variant) ?? "-"}</td>
                <td />
              </tr>
            ))
          : null}
      </React.Fragment>
    );
  }

  return (
    <div className="facility-product-table-scroll">
      <DataTable
        className="facility-product-table"
        stickyHeader
        noRoundedTop
        overflow
        loading={products === undefined}
        columns={[
          {
            label: "",
            content: (
              <span className="facility-product-table-name">
                <Checkbox
                  id="product-select-all"
                  size="small"
                  checked={allSelected}
                  onChange={() => onSelectedIdsChange(allSelected ? [] : selectableIds)}
                />
                {renderSortHeader("title", t("secure.facility.product.product_table.001"))}
              </span>
            ),
          },
          { label: t("SKU") },
          { label: t("Type") },
          { label: t("Price"), content: renderSortHeader("price", t("Price")) },
          { label: t("Inventory"), content: renderSortHeader("inventory", t("Inventory")) },
          { label: t("Location") },
        ]}
      >
        {products?.length === 0 ? (
          <tr>
            <td colSpan={6} className="facility-product-table-empty">
              {t("No products found")}
            </td>
          </tr>
        ) : (
          products?.map(product => renderProductRows(product))
        )}
      </DataTable>
    </div>
  );
}
